/* eslint-disable i18next/no-literal-string */
import { Helmet } from 'react-helmet-async';
import { getCurrentEnvironment } from '../common/utils/environmentUtils';
import { getDomainName, getEnv, getTitlePrefix, isDev, isLocal, isProd, isStaging } from '../config/env';
import { useAuthUser } from '../hooks/useUser';

const GIT_SHA = import.meta.env.VITE_GIT_SHA;
const GIT_TAG = import.meta.env.VITE_GIT_TAG;
const BUILD_DATE = import.meta.env.VITE_BUILD_DATE;

const DebugPage = () => {
  const { token, currentUser } = useAuthUser();

  if (!token) return null;

  const result = window.navigator.userAgent;

  // Get current timezone
  const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Get browser name and version
  const getBrowserInfo = () => {
    const userAgent = window.navigator.userAgent;
    let browserName = 'Unknown';
    let fullVersion = 'Unknown';
    let tempVersion;

    if (userAgent.indexOf('Chrome') > -1) {
      browserName = 'Chrome';
      tempVersion = userAgent.split('Chrome/')[1];
    } else if (userAgent.indexOf('Firefox') > -1) {
      browserName = 'Firefox';
      tempVersion = userAgent.split('Firefox/')[1];
    } else if (userAgent.indexOf('Safari') > -1 && userAgent.indexOf('Chrome') === -1) {
      browserName = 'Safari';
      tempVersion = userAgent.split('Version/')[1];
    } else if (userAgent.indexOf('Edg') > -1) {
      browserName = 'Edge';
      tempVersion = userAgent.split('Edg/')[1];
    }

    if (tempVersion) {
      fullVersion = tempVersion.split(' ')[0];
    }

    return { browserName, fullVersion };
  };

  const { browserName, fullVersion } = getBrowserInfo();
  const environment = getCurrentEnvironment();

  const items: { label: string; value: string }[] = [
    { label: 'Current User Email:', value: currentUser?.email ?? 'N/A' },
    { label: 'User Agent:', value: result },
    { label: 'Browser:', value: `${browserName} ${fullVersion}` },
    { label: 'Current Time:', value: new Date().toISOString() },
    { label: 'Timezone:', value: currentTimezone },
    { label: 'Git SHA:', value: GIT_SHA },
    { label: 'Git Tag:', value: GIT_TAG },
    { label: 'Built At:', value: BUILD_DATE },
    { label: 'getCurrentEnvironment:', value: environment.toString() },
    { label: 'getEnv:', value: getEnv() },
    { label: 'getDomainName:', value: getDomainName() },
    { label: 'isLocal:', value: isLocal().toString() },
    { label: 'isDev:', value: isDev().toString() },
    { label: 'isStaging:', value: isStaging().toString() },
    { label: 'isProd:', value: isProd().toString() },
  ];

  return (
    <>
      <Helmet>
        <title>{getTitlePrefix()} Debug Page</title>
      </Helmet>
      <div className="min-h-screen bg-gray-100 py-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-lg bg-white p-8 shadow-lg">
            <h1 className="mb-4 text-2xl font-semibold text-gray-900">Debug Information</h1>
            <p className="mb-6 text-gray-700">
              You&apos;ve been sent to this page which means you need to debug something.
            </p>
            <div className="space-y-4">
              {items.map((item) => (
                <div key={item.label}>
                  <strong className="text-gray-800">{item.label}</strong>
                  <pre className="mt-1 rounded bg-gray-50 p-2 text-gray-900">{item.value}</pre>
                </div>
              ))}
            </div>
            <div className="mt-6">
              You should take a screenshot of this page and send it to the developer or support agent.{' '}
              <a
                href="https://www.take-a-screenshot.org/"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:underline"
              >
                How to take a screenshot?
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DebugPage;
